/* eslint-disable react/jsx-indent */
import { Typography, Divider, IconButton, Stack, Button, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useState, useEffect } from 'react';
import { mapByRefValue } from '../../components/Details/Details';
import { generalMapping } from '../../lib/mappings/generalMapping';
import { useTranslation } from 'react-i18next';
import { formatMediaDataOmeka, transformGreekToEnglish } from '../../lib/helperFuncs';
import LinkButton from '../../components/Buttons/LinkButton';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router-dom';
import HttpService from '../../services/HttpService';
import LocationDetailsSkeleton from '../../components/Skeletons/LocationDetailsSkeleton';
import DetailsWrapper from '../../components/Details/DetailsWrapper';
import CarouselContainerForDetails from '../../components/Carousel/CarouselContainerForDetails';
import { _basicInfo, _contactDetails, _usefulInfo } from '../../lib/static/professionals/details';
import UserService from '../../services/UserService';
import { IntExt, Permissions } from '../../lib/permissions';
import { initialValues } from '../../lib/static/professionals/initialValues';
import { validationSchemaCommForm } from '../../lib/static/professionals/validationSchema';
import { IMediaAttachments } from '../ΟΑ/Tekmirio/types';
import Attachments from '../../components/Attachments/Attachments';
import DialogForm from '../../components/Dialog/DialogForm';
import { communicationFields } from '../../lib/static/professionals/formFields';
import { FormikContextType, FormikValues } from 'formik';
import { useContext } from 'react';
import BreadcrumbContext from '../../components/context/BreadcrumbContext';
import Theme from '../../Themes/main.theme';
import i18n from '../../i18n';
import EmptyPage from '../../components/EmptyPage/EmptyPage';
import { useFromAccessLogic } from '../../hooks/useFromAccessLogic';
import { AppleTitleNamingKeys } from '../../lib/generalTypes';

function ProfDetails() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingContact, setIsLoadingContact] = useState<boolean>(false);
  const [media, setMedia] = useState<any[]>([]);
  const [contactDetails, setContactDetails] = useState<any>(_contactDetails);
  const [usefulInfo, setUsefulInfo] = useState<any>(_usefulInfo);
  const [basicInfo, setBasicInfo] = useState<any>(_basicInfo);
  const [title, setTitle] = useState<string>('');
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [favoriteError, setFavoriteError] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<IMediaAttachments[]>([]);
  const [isApplicant, setIsApplicant] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | any>();
  const [isFormPublishedContact, setIsFormPublishedContact] = useState<boolean>(false);

  const {
    availableForms: availableFormsContact,
    isLoading: isFormPublishedContactLoading
  } = useFromAccessLogic(AppleTitleNamingKeys.ContactProfessional);
  const {
    isLoading: isFormPublishedLoading
  } = useFromAccessLogic(AppleTitleNamingKeys.ContactProfessional);


  useEffect(() => {
    setIsFormPublishedContact(availableFormsContact?.is_published);
  }, [isFormPublishedLoading, isFormPublishedContactLoading]);


  const { setName } = useContext(BreadcrumbContext.BreadcrumbProvider.context);

  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const is_external = UserService.hasRole([IntExt.External]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const fetchProf = async () => {
    try {
      setName('');
      const resp = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/professionals/${id}`);
      const data = resp.data;
      setIsFavorite(data?.item?.is_favorite);

      setIsApplicant(resp.data.is_applicant_user);

      const _mappedMetadata: any = generalMapping(data.item);
      setName(i18n.language === 'en'
        ? transformGreekToEnglish(`${_mappedMetadata.profTitle}`)
        : _mappedMetadata.profTitle);
      document.title = i18n.language === 'en'
        ? transformGreekToEnglish(`${_mappedMetadata.profTitle}`)
        : _mappedMetadata.profTitle;
      setTitle(i18n.language === 'en'
        ? transformGreekToEnglish(`${_mappedMetadata.profTitle}`)
        : _mappedMetadata.profTitle);
      setUsefulInfo(mapByRefValue(_mappedMetadata, _usefulInfo(_mappedMetadata)).map(x => {
        return {
          ...x,
          name: t(`${x.name}`),
          value: Array.isArray(x?.value)
            ? x?.value?.map((value: any) => t(value)).join(', ')
            : t(`${x?.value}`)
        };
      }));
      setContactDetails(mapByRefValue(_mappedMetadata, _contactDetails(_mappedMetadata)).map(x => {
        return {
          ...x,
          name: t(`${x.name}`),
          ...(i18n.language === 'en' && { value: transformGreekToEnglish(`${x.value}`) })
        };
      }));
      setBasicInfo(mapByRefValue(_mappedMetadata, _basicInfo(_mappedMetadata)).map(x => {
        return {
          ...x,
          name: t(`${x.name}`),
          ...(x.refValue === 'professionalAddress' && { value: transformGreekToEnglish(`${x.value}`) })
        };
      }));

      const { images, videos, thumbnail, attachments } = formatMediaDataOmeka(data);

      setAttachments(attachments);
      setMedia([...(thumbnail
        ? [thumbnail]
        : []), ...images, ...videos]);
      setIsLoading(false);
    }
    catch (error) {
      console.error(error);
      setErrorMsg(t('Η σελίδα που ζητήσατε δεν βρέθηκε.'));
      setIsLoading(false);
    }
  };

  const onSubmitFavorite = async (isFavorite: boolean) => {
    try {
      if (!(UserService.isAuthenticated())) {
        enqueueSnackbar(t('Πρέπει να είστε συνδεδεμένος για να προσθέσετε επαγγελματία στα αγαπημένα σας'), { variant: 'error' });
        return;
      }
      const resp = await HttpService.getAxiosClient().patch(`${process.env.REACT_APP_BACKEND_URL}/professionals/favorites/${id}`, { is_favorite: isFavorite });
      if (resp.data.is_favorite) {
        enqueueSnackbar(`${t('O επαγγελματίας')} ${resp.data.title} ${t('προστέθηκε στα αγαπημένα σας')}`, { variant: 'success' });
      } else {
        enqueueSnackbar(`${t('O επαγγελματίας')} ${resp.data.title} ${t('αφαιρέθηκε από τα αγαπημένα σας')}`, { variant: 'info' });
      }
    } catch (error) {
      setFavoriteError(!favoriteError);
      console.error(error);
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'), { variant: 'error' });
    }
  };

  const onSubmitForm = async (values: FormikContextType<FormikValues>) => {
    setIsLoadingContact(true);
    try {
      const resp = await HttpService.getAxiosClient().post(`${process.env.REACT_APP_BACKEND_URL}/forms/communication-prof`, { ...values, prof_id: Number(id) });
      enqueueSnackbar(`${t('Η Αίτηση Επικοινωνίας με τον Επαγγελματία / Επιχείρηση')} ${resp.data} ${t('στάλθηκε επιτυχώς!')}`, { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'), { variant: 'error' });
    }
    setOpen(false);
    setIsLoadingContact(false);
  };

  useEffect(() => {
    fetchProf();
  }, [favoriteError]);

  const _communicationFields = communicationFields();
  
  const resumeDoc =
    <Box>
      <Typography mt={3} variant='h3'
        style={{ marginLeft: '10px' }}>
        {t('Βιογραφικό Σημείωμα')}
      </Typography>
      <Box mb={3}>
        <Attachments attachments={attachments} />
      </Box>
    </Box>;

  return (
    errorMsg ?
      <Box id='container-details'
        sx={{ m: 2, height: '600px' }}
      >
        <EmptyPage errorMsg={errorMsg} />
      </Box>
      :
      <LocationDetailsSkeleton isLoading={isLoading} length={4}>


        <Box id='container-details' mt={2}
          width='100%'>
          <DialogForm title={t('Επικοινωνία με τον Επαγγελματία / Επιχείρηση') as string}
            isLoading={isLoadingContact}
            initialValues={initialValues}
            validationSchema={validationSchemaCommForm}
            fields={_communicationFields}
            open={open}
            handleClose={() => setOpen(false)}
            onAccept={onSubmitForm}
          />
          <Box display='flex' justifyContent='space-between'>
            <Box>
              <Stack flexDirection='row'>
                <Typography variant='h2' sx={{ mt: 1, color: Theme.labelColor?.main, mr: 2 }}>
                  {title}
                </Typography>
                {UserService.hasRole([Permissions
                  .PermissionExternalProfessionalsFavoritesUpdateMy])
                  && is_external ?
                  <IconButton sx={{ mr: 3, ml: 3 }}
                    onClick={() => {
                      (UserService.isAuthenticated()) && setIsFavorite(!isFavorite);
                      onSubmitFavorite(!isFavorite);
                    }
                    }>
                    {isFavorite === true
                      ? <FavoriteIcon color='error'
                        sx={{ border: 'solid 2px #bababa', borderRadius: '50%', padding: 0.5 }} />
                      : <FavoriteBorderIcon sx={{ border: 'solid 2px #bababa', borderRadius: '50%', padding: 0.5 }} />
                    }
                  </IconButton>
                  : null}
              </Stack>
            </Box>
            <Box display='flex' justifyContent='end'
              gap={2}
              flexWrap='wrap'>
              {(isApplicant && UserService.hasRole([
                Permissions.PermissionExternalProfessionalApplicationModificationCreateMy
              ])) &&

                <LinkButton disabled={!(UserService.hasRole([
                  Permissions.PermissionExternalProfessionalApplicationModificationCreateMy
                ]) && isApplicant)}
                link={pathname.split('/').filter(Boolean)[0] === 'admin'
                  ? `/admin/professionals/edit/${id}?omeka=true`
                  : `/professionals/edit/${id}?omeka=true`
                }
                color='primary'
                variant='contained'
                tooltipTitle={!UserService.isAuthenticated()
                  ? t('Παρακαλούμε συνδεθείτε για να μπορέσετε να πραγματοποιήσετε αυτή τη λειτουργία')
                  : !(UserService.hasRole([
                    Permissions.PermissionExternalProfessionalApplicationModificationCreateMy
                  ])
                      && isApplicant
                  )
                    ? t('Δεν έχετε δικαίωμα επεξεργασίας του Επαγγελματία')
                    : ''}
                sx={{ borderRadius: '24px' }}>
                  {t('Επεξεργασία Στοιχείων')}
                </LinkButton>
              }

              {isFormPublishedContact
                && <>
                  <Tooltip PopperProps={{ disablePortal: true }}
                    title={!is_external
                      ? t('Παρακαλούμε συνδεθείτε για να μπορέσετε να πραγματοποιήσετε αυτή τη λειτουργία')
                      : ''}
                  >
                    <span>
                      <Button
                        disabled={!is_external}
                        variant='contained'
                        sx={{ borderRadius: '24px' }}
                        color='primary'
                        onClick={handleClickOpen}
                      >
                        {t('Επικοινωνία')}
                      </Button>
                    </span>
                  </Tooltip>

                </>}

            </Box>
          </Box>
          <Divider sx={{ mt: 1, mb: 5 }} />
          <Box mt={5}>
            <DetailsWrapper title={t('Στοιχεία Επικοινωνίας')} detailsProps={{ rows: contactDetails, removeLines: true }} />
            <DetailsWrapper title={t('Βασικά Στοιχεία')} detailsProps={{ rows: basicInfo, removeLines: true }} />
            <DetailsWrapper title={t('Χρήσιμες Πληροφορίες')} detailsProps={{ rows: usefulInfo, removeLines: true }} />
            {media.length > 0
              ? <>
                <Box m={1} mt={'24px'}
                  minWidth={350}>
                  <CarouselContainerForDetails title={'Φωτογραφίες / Βίντεο'} media={media} />
                </Box>
              </>
              : null
            }
            {attachments && attachments.length > 0
              ? resumeDoc
              : null
            }
          </Box>
        </Box>
      </LocationDetailsSkeleton>
  );
}

export default ProfDetails; 